import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  DEFAULT_BACKGROUND_COLORS,
  DEFAULT_FONT_SIZE,
  DEFAULT_INNER_RADIUS,
  DEFAULT_INNER_BORDER_COLOR,
  DEFAULT_INNER_BORDER_WIDTH,
  DEFAULT_OUTER_BORDER_COLOR,
  DEFAULT_OUTER_BORDER_WIDTH,
  DEFAULT_RADIUS_LINE_COLOR,
  DEFAULT_RADIUS_LINE_WIDTH,
  DEFAULT_SELECTOR_COLOR,
  DEFAULT_TEXT_COLORS,
  DEFAULT_TEXT_DISTANCE,
} from './constants';
import { getRotationDegrees } from './utils';
import { WheelCanvas } from './components';
import {
  RotationContainer,
  RouletteContainer,
  RouletteSelectorVector,
} from './styles';

const STARTED_SPINNING = 'started-spinning';

const CONTINUE_SPINNING_TIME = 750;
const START_SPINNING_TIME = 2600;
const STOP_SPINNING_TIME = 8000;

export const Wheel = ({
  backgroundColors = DEFAULT_BACKGROUND_COLORS,
  children,
  data,
  fontSize = DEFAULT_FONT_SIZE,
  innerRadius = DEFAULT_INNER_RADIUS,
  innerBorderColor = DEFAULT_INNER_BORDER_COLOR,
  innerBorderWidth = DEFAULT_INNER_BORDER_WIDTH,
  mustStartSpinning,
  onStopSpinning = () => null,
  outerBorderColor = DEFAULT_OUTER_BORDER_COLOR,
  outerBorderWidth = DEFAULT_OUTER_BORDER_WIDTH,
  perpendicularText = false,
  prizeNumber,
  radiusLineColor = DEFAULT_RADIUS_LINE_COLOR,
  radiusLineWidth = DEFAULT_RADIUS_LINE_WIDTH,
  selectorColor = DEFAULT_SELECTOR_COLOR,
  textColors = DEFAULT_TEXT_COLORS,
  textDistance = DEFAULT_TEXT_DISTANCE,
}) => {
  const [wheelData, setWheelData] = useState([...data]);
  const [startRotationDegrees, setStartRotationDegrees] = useState(0);
  const [finalRotationDegrees, setFinalRotationDegrees] = useState(0);
  const [hasStartedSpinning, setHasStartedSpinning] = useState(false);
  const [hasStoppedSpinning, setHasStoppedSpinning] = useState(false);
  const [isCurrentlySpinning, setIsCurrentlySpinning] = useState(false);
  const [isDataUpdated, setIsDataUpdated] = useState(false);

  const mustStopSpinning = useRef(false);

  useEffect(() => {
    const dataLength = data.length;
    const wheelDataAux = [{ option: '' }];
    for (let i = 0; i < dataLength; i++) {
      wheelDataAux[i] = {
        ...data[i],
        style: {
          backgroundColor:
            data[i].style?.backgroundColor ||
            backgroundColors[i % backgroundColors.length],
          fontSize: data[i].style?.fontSize,
          textColor:
            data[i].style?.textColor || textColors[i % textColors.length],
        },
      };
    }
    setWheelData([...wheelDataAux]);
    setIsDataUpdated(true);
  }, [data, backgroundColors, textColors]);

  useEffect(() => {
    if (mustStartSpinning && !isCurrentlySpinning) {
      const startSpinning = () => {
        setHasStartedSpinning(true);
        setHasStoppedSpinning(false);
        mustStopSpinning.current = true;
        setTimeout(() => {
          if (mustStopSpinning.current) {
            mustStopSpinning.current = false;
            setHasStartedSpinning(false);
            setHasStoppedSpinning(true);
            onStopSpinning();
          }
        }, START_SPINNING_TIME + CONTINUE_SPINNING_TIME + STOP_SPINNING_TIME - 300);
      };

      setIsCurrentlySpinning(true);
      startSpinning();
      const finalRotationDegreesCalculated = getRotationDegrees(
        prizeNumber,
        data.length
      );
      setFinalRotationDegrees(finalRotationDegreesCalculated);
    }
  }, [mustStartSpinning, data.length, prizeNumber, isCurrentlySpinning, onStopSpinning]);

  useEffect(() => {
    if (hasStoppedSpinning) {
      setIsCurrentlySpinning(false);
      setStartRotationDegrees(finalRotationDegrees);
    }
  }, [hasStoppedSpinning, finalRotationDegrees]);

  const getRouletteClass = () => {
    if (hasStartedSpinning) {
      return STARTED_SPINNING;
    }
    return '';
  };

  if (!isDataUpdated) {
    return null;
  }

  return (
    <RouletteContainer>
      <RotationContainer
        className={getRouletteClass()}
        startSpinningTime={START_SPINNING_TIME}
        continueSpinningTime={CONTINUE_SPINNING_TIME}
        stopSpinningTime={STOP_SPINNING_TIME}
        startRotationDegrees={startRotationDegrees}
        finalRotationDegrees={finalRotationDegrees}
      >
        <WheelCanvas
          width="900"
          height="900"
          data={wheelData}
          outerBorderColor={outerBorderColor}
          outerBorderWidth={outerBorderWidth}
          innerRadius={innerRadius}
          innerBorderColor={innerBorderColor}
          innerBorderWidth={innerBorderWidth}
          radiusLineColor={radiusLineColor}
          radiusLineWidth={radiusLineWidth}
          fontSize={fontSize}
          perpendicularText={perpendicularText}
          textDistance={textDistance}
        />
      </RotationContainer>
      <RouletteSelectorVector
        viewBox="0 0 272 269"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M129.297 12.4171C90.9339 19.3961 63.8969 41.7161 48.3919 79.2051C33.9579 114.104 21.2949 166.576 12.2859 228.812C9.34588 249.127 9.26888 254.999 11.8989 258.541C15.2449 263.049 17.9229 263.475 30.5489 261.508C112.53 248.74 138.631 243.221 173.186 231.35C200.86 221.843 212.066 216.342 224.606 206.107C245.645 188.936 260.04 158.415 261.977 126.873C263.492 102.193 254.922 76.1831 237.755 53.3631C222.122 32.5831 197.288 17.5531 170.047 12.3851C160.242 10.5251 139.607 10.5411 129.297 12.4171Z"
          fill={selectorColor}
        />
      </RouletteSelectorVector>
      {children}
    </RouletteContainer>
  );
};

Wheel.propTypes = {
  backgroundColors: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
  data: PropTypes.arrayOf(PropTypes.object),
  fontSize: PropTypes.number,
  innerBorderColor: PropTypes.string,
  innerBorderWidth: PropTypes.number,
  innerRadius: PropTypes.number,
  mustStartSpinning: PropTypes.bool,
  onStopSpinning: PropTypes.func,
  outerBorderColor: PropTypes.string,
  outerBorderWidth: PropTypes.number,
  perpendicularText: PropTypes.bool,
  prizeNumber: PropTypes.number,
  radiusLineColor: PropTypes.string,
  radiusLineWidth: PropTypes.number,
  selectorColor: PropTypes.string,
  textColors: PropTypes.arrayOf(PropTypes.string),
  textDistance: PropTypes.number,
};
